import { useState } from "react";
import { FormValues } from "@/types/loan";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "@/hooks/useAnalytics";
import { processApplicationWaterfall } from "@/services/waterfall/applicationWaterfall";
import { 
  sanitizeString,
  sanitizeEmail,
  sanitizePhoneNumber,
  generateSecureToken
} from "@/utils/security";
import { CSRFProtection } from "@/utils/csrf";
import { rateLimiter } from "@/utils/rateLimiting";
import { loadApplicationData, saveApplicationData } from "@/utils/applicationStorage";
import { validateFormData } from "@/utils/formValidation";
import { saveLoanLead, saveMarketingContact } from "@/services/applicationSubmission";

export { loadApplicationData, saveApplicationData };

export const useApplicationSubmit = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const handleSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      console.log("Validating and submitting application:", data);
      
      // Enhanced rate limiting with multiple identifiers
      const rateLimitChecks = [
        rateLimiter.checkLimit(data.email, 3, 10 * 60 * 1000), // 3 per 10 minutes per email
        rateLimiter.checkLimit(`${data.firstName}-${data.lastName}`, 5, 15 * 60 * 1000) // 5 per 15 minutes per name
      ];
      
      const failedCheck = rateLimitChecks.find(check => !check.allowed);
      if (failedCheck) {
        const resetTime = failedCheck.resetTime ? new Date(failedCheck.resetTime).toLocaleTimeString() : 'tuntematon';
        toast({
          title: "Liian monta yritystä",
          description: `Odota ${resetTime} asti ennen uutta yritystä`,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      
      // Validate and sanitize form data
      const validation = validateFormData(data);
      if (!validation.valid) {
        toast({
          title: "Lomakkeen tarkistus epäonnistui",
          description: validation.message,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      
      // Generate CSRF token for this request
      const csrfToken = CSRFProtection.generateToken();
      
      // Sanitize data
      const sanitizedData = {
        ...data,
        firstName: sanitizeString(data.firstName),
        lastName: sanitizeString(data.lastName),
        email: sanitizeEmail(data.email),
        phoneNumber: sanitizePhoneNumber(data.phoneNumber)
      };
      
      saveApplicationData(sanitizedData);
      
      trackEvent('loan_application_submitted', {
        loanType: sanitizedData.loanType || 'consumer',
        loanAmount: sanitizedData.loanAmount,
        loanTerm: sanitizedData.loanTerm,
        loanPurpose: sanitizedData.loanPurpose
      });

      // Save loan lead to Supabase database
      try {
        await saveLoanLead(sanitizedData);
      } catch (error) {
        console.error("Error saving loan lead:", error);
        // Don't stop the flow if loan lead save fails
      }

      // Save marketing contact if consent given
      try {
        await saveMarketingContact(sanitizedData);
      } catch (error) {
        console.error("Error saving marketing contact:", error);
        // Don't stop the flow if marketing save fails
      }

      // Generate a secure token for the application
      const token = generateSecureToken();
      console.log("Using secure token for application:", token);
      
      // Process application through waterfall logic with enhanced security
      const waterfallResult = await processApplicationWaterfall(token, sanitizedData);
      console.log("Waterfall result:", waterfallResult);
      
      // Track the redirect event
      trackEvent('application_redirect', {
        destination: waterfallResult.provider,
        provider: waterfallResult.provider,
        token: waterfallResult.token || token,
        auto_redirect: true
      });
      
      // Clear CSRF token after successful submission
      CSRFProtection.clearToken();
      
      // Show processing message for all applications
      toast({
        title: "Hakemustasi käsitellään",
        description: "Siirretään sinua lainahakemussivulle...",
      });

      // Add delay before redirect for better UX
      setTimeout(() => {
        // Direct to /hakemus-1 with parameters for AXO applications
        if (waterfallResult.provider === 'axo') {
          const params = new URLSearchParams({
            amount: sanitizedData.loanAmount.toString(),
            term: sanitizedData.loanTerm.toString(),
            purpose: sanitizedData.loanPurpose || 'Muu',
            token: encodeURIComponent(waterfallResult.token || token)
          });
          navigate(`/hakemus-1?${params.toString()}`);
        } else {
          // Other providers go to SortterPage
          navigate(`/lainahakemus?provider=${waterfallResult.provider}&token=${encodeURIComponent(waterfallResult.token || token)}`);
        }
      }, 2000);
      
    } catch (error: any) {
      console.error("Error in application submission:", error);
      setSubmitError("Hakemuksen lähettämisessä tapahtui virhe. Yritä uudelleen tai ota yhteyttä asiakaspalveluun.");
      
      // Clear potentially compromised CSRF token
      CSRFProtection.clearToken();
      
      // Redirect to thank you page as fallback
      navigate('/kiitos');
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, submitError, handleSubmit };
};
